import $ from 'jquery';
import _ from 'underscore';
import Backbone from "backbone";
import SequencerNode from "models/audioEditor/sequencerNodes/SequencerNode";
import StartWrapSequencerNode from "models/audioEditor/sequencerNodes/StartWrapSequencerNode";
import EndWrapSequencerNode from "models/audioEditor/sequencerNodes/EndWrapSequencerNode";
import SequencerOperationUtils from "models/audioEditor/sequencerOperation/SequencerOperationUtils";
import SequencerNodeCloner from "models/audioEditor/sequencerNodes/SequencerNodeCloner";
import RSVP from "rsvp";

const CLASSNAME = "ReplacementSequencerNode";
const ReplacementSequencerNode =  SequencerNode.extend({
    idAttribute: "id",


    constructor: function(attributes, options) {
        SequencerNode.apply(this, [attributes, options]);
        this._arrayOfNodeReplaced = [];
        
        // Forgiveness Mode: Controls shared node restoration during bulk operations
        // See BULK_DELETION_FORGIVENESS_SYSTEM.md for complete documentation
        this._allowSharedNodeRestore = false; // Default to strict mode
    },

    getConstructor : function(){
        return ReplacementSequencerNode;
    },



    afterInstanceCreatedFromConstructorForCloning : function(createdClonedInstance, sequencerNodeCidToCloneNodeMap){
        const cloneReplacedNodes = this._arrayOfNodeReplaced.map(function(nodeToClone){
            return SequencerNodeCloner.getInstance().cloneLinkedSequencerNodes(nodeToClone,null, sequencerNodeCidToCloneNodeMap);
        });
        createdClonedInstance._arrayOfNodeReplaced = cloneReplacedNodes;
        createdClonedInstance._allowSharedNodeRestore = this._allowSharedNodeRestore;
    },

    cloneJSON: function(){
        const objJson = SequencerNode.prototype.cloneJSON.call(this);
        objJson.type = ReplacementSequencerNode.CLASSNAME;
        objJson.allowSharedNodeRestore = this._allowSharedNodeRestore;
        return objJson;
    },

    getSerializedData : function(){
        const serializedData = SequencerNode.prototype.getSerializedData.call(this);
        serializedData.arrayOfNodeCidReplaced  = [];
        for(let i =0; i < this._arrayOfNodeReplaced.length; i++ ){
            const nodeReplaced = this._arrayOfNodeReplaced[i];
            //serializedData.arrayOfNodeReplaced.push(nodeReplaced.getSerializedData());
            serializedData.arrayOfNodeCidReplaced.push(nodeReplaced.cid);
        }
        serializedData.allowSharedNodeRestore = this._allowSharedNodeRestore;
        return serializedData;
    },

    restoreFromSerializedData: function(serializedData, audioSegmentFactoryInstance, audioFilterFactoryInstance, additionalSpeakerInfoArray){
        this._allowSharedNodeRestore = serializedData.allowSharedNodeRestore || false;
        return SequencerNode.prototype.restoreFromSerializedData.call(this, serializedData, audioSegmentFactoryInstance, audioFilterFactoryInstance, additionalSpeakerInfoArray);
    },

    afterAllInstancesCreatedFromSerializedData : function(serializedData, sequencerNodeCidToSequencerNodeMap){
        SequencerNode.prototype.afterAllInstancesCreatedFromSerializedData.call(this, serializedData, sequencerNodeCidToSequencerNodeMap);
        if(serializedData.arrayOfNodeCidReplaced){
            this._arrayOfNodeReplaced =  [];
            serializedData.arrayOfNodeCidReplaced.map((function(nodeCid){
                const replacedNode = sequencerNodeCidToSequencerNodeMap[nodeCid];
                if(replacedNode){
                    this._arrayOfNodeReplaced.push(replacedNode);
                }else{
                    window.alertErrorMessage("Something went wrong during the initialization of this node")
                }
            }).bind(this))
        }
    },


    getArrayOfNodeReplaced: function(){
        return this._arrayOfNodeReplaced;
    },

    setAllowSharedNodeRestore: function(allowSharedNodeRestore){
        this._allowSharedNodeRestore = !!allowSharedNodeRestore;
    },

    getAllowSharedNodeRestore: function(){
        return this._allowSharedNodeRestore;
    },

    replaceNodes : function(arrayOfNodeToReplace){

        if(this.getArrayOfNodeReplaced() && this.getArrayOfNodeReplaced().length > 0){
            this.revertReplacement();
        }

        // Check if nodes being replaced are already deleted/disconnected
        let nodesToReplace = arrayOfNodeToReplace;
        const alreadyDeletedNodes = arrayOfNodeToReplace.filter(node => !node.isAddedToSequencer());
        
        if(alreadyDeletedNodes.length > 0) {
            if(this._allowSharedNodeRestore) {
                // FORGIVENESS MODE: Filter out already deleted nodes instead of throwing error
                // This allows overlapping bulk deletions where some nodes were already deleted by previous operations
                // See BULK_DELETION_FORGIVENESS_SYSTEM.md for complete explanation
                console.warn("replaceNodes: Filtering out already deleted nodes in forgiveness mode", {
                    alreadyDeletedNodeCids: alreadyDeletedNodes.map(n => n.cid),
                    totalNodesRequested: arrayOfNodeToReplace.length,
                    allowSharedNodeRestore: this._allowSharedNodeRestore
                });
                
                nodesToReplace = arrayOfNodeToReplace.filter(node => node.isAddedToSequencer());
                
                if(nodesToReplace.length === 0) {
                    console.info("replaceNodes: All nodes already deleted in forgiveness mode, skipping replacement");
                    return; // Nothing to replace
                }
            } else {
                // STRICT MODE: Throw error as before
                console.error("replaceNodes: Attempting to replace already deleted nodes", {
                    alreadyDeletedNodeCids: alreadyDeletedNodes.map(n => n.cid)
                });
                throw new Error("Cannot replace nodes that are already deleted. This indicates overlapping bulk deletion operations.");
            }
        }

        this._arrayOfNodeReplaced =  nodesToReplace;

        const startNode = nodesToReplace[0];
        const endNode =  nodesToReplace[nodesToReplace.length -1];

        const startNodeReplacedInitialPrevious =  startNode.getPrevious();
        const endNodeReplacedInitialNext = endNode.getNext();

        SequencerOperationUtils.getInstance().removeAudioNodeArray(this.getSequencer(), this._arrayOfNodeReplaced);
        
        if(startNodeReplacedInitialPrevious ){
            this.insertAfter(startNodeReplacedInitialPrevious);
        }else{
            if(endNodeReplacedInitialNext){
                this.insertBefore(endNodeReplacedInitialNext);
            }else{
                // first node and end node are null
            }
        }

        this.addToSequencer();
        if(startNode == this.getSequencer().getFirstNode()){
            this.getSequencer().setFirstNode(this);
        }
        if(endNode == this.getSequencer().getLastNode()){
            this.getSequencer().setLastNode(this);
        }
    },


    revertReplacement : function(){
        const previousNode = this.getPrevious();
        const nextNode = this.getNext();

        let nodesToRestore = this.getArrayOfNodeReplaced();

        // Guard against double-revert: _arrayOfNodeReplaced is nulled at the end of this method,
        // so a second call (e.g. nested revert via restoreDeleteNode → revertDeletedNodeCreation)
        // would otherwise dereference null on nodesToRestore[0].
        if(!nodesToRestore || nodesToRestore.length === 0) {
            console.warn("revertReplacement: nothing to restore (already reverted?)", {
                cid: this.cid,
                hasArrayOfNodeReplaced: !!this._arrayOfNodeReplaced
            });
            this.setHidden(true);
            this._arrayOfNodeReplaced = null;
            return;
        }

        // FORGIVENESS MODE: Handle overlapping node restoration during bulk operations
        // This prevents "next node is already set" errors when undoing overlapping bulk deletions
        // See BULK_DELETION_FORGIVENESS_SYSTEM.md for detailed explanation
        if(this._allowSharedNodeRestore) {
            // Filter out nodes that are already restored (have next/previous links)
            const originalNodeCount = nodesToRestore.length;
            nodesToRestore = nodesToRestore.filter(node => {
                const isAlreadyRestored = node.getNext() !== null || node.getPrevious() !== null || node.isAddedToSequencer();
                if(isAlreadyRestored) {
                    console.warn("revertReplacement: Skipping already restored node in forgiveness mode", {
                        nodeCid: node.cid,
                        hasNext: node.getNext() !== null,
                        hasPrevious: node.getPrevious() !== null,
                        isAddedToSequencer: node.isAddedToSequencer()
                    });
                }
                return !isAlreadyRestored;
            });
            
            if(originalNodeCount !== nodesToRestore.length) {
                console.info("revertReplacement: Forgiveness mode filtered out already restored nodes", {
                    originalCount: originalNodeCount,
                    filteredCount: nodesToRestore.length,
                    allowSharedNodeRestore: this._allowSharedNodeRestore
                });
            }
            
            // If all nodes are already restored, nothing to do
            if(nodesToRestore.length === 0) {
                console.info("revertReplacement: All nodes already restored, completing operation");
                this.setHidden(true);
                this._arrayOfNodeReplaced = null;
                return;
            }
        }

        const startReplacedNode = nodesToRestore[0];
        const endReplacedNode = nodesToRestore[nodesToRestore.length - 1];

        SequencerOperationUtils.getInstance().removeAudioNodeArray(this.getSequencer(), [this]);
        this.setHidden(true);
        const sequencerNodeToInsertNextTo = previousNode?previousNode: nextNode;
        const insertBefore =previousNode?false: true;
        SequencerOperationUtils.getInstance().insertAudioNodeArray(this.getSequencer(), nodesToRestore, sequencerNodeToInsertNextTo, insertBefore);
        this._arrayOfNodeReplaced = null;
        if(this == this.getSequencer().getFirstNode()){
            this.getSequencer().setFirstNode(startReplacedNode);
        }
        if(this == this.getSequencer().getLastNode()){
            this.getSequencer().setLastNode(endReplacedNode);
        }
    },

    getContent: function(addSpaceAroundContent){
        const content = this.getArrayOfNodeReplaced().reduce((acc, nodeReplaced)=>{
            if(addSpaceAroundContent){
                return acc +(nodeReplaced.getContent(addSpaceAroundContent)|| "");  
            }else{
                return acc + " " +(nodeReplaced.getContent(addSpaceAroundContent)|| "");    
            }
        }, "");
        /*if(addSpaceAroundContent){
            return ` ${content.trim()} `;
        }else{
            return content;
        }*/
        return content;
    },



    /*_moveAllWrapNodesThatHaveOtherNodeOutsideOfTheListOfNodeToWrap : function(){
        const startWrapNodeToMoveArray = [];
        const endWrapNodeToMoveArray = [];
        const nodeToReplace = this._startNodeReplaced
        while(nodeToReplace){
            if(nodeToReplace instanceof StartWrapSequencerNode){
                startWrapNodeToMoveArray.push(nodeToReplace);
            }else{
                if(nodeToReplace instanceof EndWrapSequencerNode){
                    const matchingStartNode = nodeToReplace.getStartWrapSequencerNode();
                    const indexOfMatchingStartNode = startWrapNodeToMoveArray.indexOf(matchingStartNode);
                    if(indexOfMatchingStartNode == -1){
                        endWrapNodeToMoveArray.push(nodeToReplace);
                    }else{
                        startWrapNodeToMoveArray.splice(indexOfMatchingStartNode, 1);
                    }
                }
            }
            if(nodeToReplace == this._endNodeReplaced){
                nodeToReplace = null;
            }else{
                nodeToReplace = nodeToReplace.getNext();
            }
        }
        const numberOfNodeToMove = startWrapNodeToMoveArray.length +endWrapNodeToMoveArray.length;
        for(let i = 0; i < startWrapNodeToMoveArray.length; i++){
            const startWrapNodeToMove = startWrapNodeToMoveArray[i];
            startWrapNodeToMove.shrinkWrapTo(this._endNodeReplaced.getNext());
        }
        for(let i = 0; i < endWrapNodeToMoveArray.length; i++){
            const endWrapNodeToMove = endWrapNodeToMoveArray[i];
            endWrapNodeToMove.shrinkWrapTo(this._startNodeReplaced.getPrevious());
        }
        return numberOfNodeToMove >0;
    },*/





});

ReplacementSequencerNode.CLASSNAME =  CLASSNAME;
export default ReplacementSequencerNode; 